/**
 * Opens one stored policy document from a link (#37141).
 *
 * The agent's endorsement email links straight to the endorsement dec rather than to the policy's
 * Documents tab, whose newest document is the packet that opens with the insured's cover letter.
 * Document keys are private, so the link names the key and this route signs it once the agent is
 * signed in (ProtectedRoute keeps the link through sign-in), then replaces the page with the PDF.
 *
 * GetDocsLambda decides whether the caller may see the document, and (mustExist) checks it exists. The key must sit under the link's
 * own account, so a link can only open a document of the account it names. When the document can't be
 * opened, the user lands on that policy's Documents tab (or the account) with a message, never a
 * blank page.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { Loading } from 'core/components/loading';
import { useToast } from 'core/components/toast';
import { getDocumentDownloadUrl, isDocumentNotFoundError } from 'core/helpers/document-download';
import { useQueryParams } from 'core/hooks/useQueryParams';

export const DOCUMENT_ROUTE_PATH = '/customer/:id/document';
export const DOCUMENT_UNAVAILABLE_MESSAGE =
  "This document couldn't be opened. It may have moved or you may not have access to it. Showing the policy's documents instead.";

// docs/<accountId>/customer/<policyId>/<file>.pdf
const policyIdFromDocumentKey = (key, accountId) => {
  const [root, keyAccountId, customer, policyId] = key.split('/');
  return root === 'docs' && keyAccountId === accountId && customer === 'customer' && policyId ? policyId : null;
};

export const DocumentRedirect = ({ match }) => {
  const accountId = match.params.id;
  const { path = '' } = useQueryParams();
  const policyId = policyIdFromDocumentKey(path, accountId);
  const { notify } = useToast();
  const [failed, setFailed] = useState(!policyId);

  useEffect(() => {
    if (!policyId) {
      if (path) {
        notify({ type: 'error', message: DOCUMENT_UNAVAILABLE_MESSAGE, durationInSeconds: 10 });
      }
      return;
    }
    let cancelled = false;
    // mustExist: a missing key answers not-found here, rather than signing and opening S3's error page.
    getDocumentDownloadUrl(path, { accountId, mustExist: true })
      .then((url) => {
        if (!cancelled) {
          window.location.replace(url);
        }
      })
      .catch((error) => {
        // A missing document is an expected outcome, handled by the fallback; anything else is a bug.
        if (!isDocumentNotFoundError(error)) {
          Sentry.captureException(error, { tags: { flow: 'documentLink' }, extra: { path } });
        }
        if (!cancelled) {
          notify({ type: 'error', message: DOCUMENT_UNAVAILABLE_MESSAGE, durationInSeconds: 10 });
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, path, policyId, notify]);

  if (!failed) {
    return <Loading noBackground />;
  }
  return (
    <Redirect
      to={
        policyId
          ? `/customer/${accountId}/policy/${policyId}?tab=documents&scrollTo=memberDocuments`
          : `/customer/${accountId}`
      }
    />
  );
};

DocumentRedirect.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired
  }).isRequired
};

export default DocumentRedirect;
